import type {
	RoadAiDataInspection,
	RoadAiDataSelection,
	RoadAiDesignRef,
	RoadAiPointCloudRef,
} from "./roadAi.types";

/** 検査に必要な最小のモデル API（PointCloud / Design のダックタイプ）。 */
interface VisiblePointCloudLike {
	getId(): number;
	getAssetId(): string;
	getName(): string;
}

interface VisibleDesignLike {
	getId(): string | number;
	getAssetId(): string;
	getName(): string;
}

export interface InspectVisibleTerrainInput {
	pointClouds: readonly VisiblePointCloudLike[];
	designs: readonly VisibleDesignLike[];
	/** チャットで「点群だけで検討したい」と伝えられた場合に true（AC-35）。 */
	pointCloudOnlyRequested?: boolean;
}

/**
 * 表示中データを検査し、実行可否と使用データ（＝モード）を確定する。
 *
 * - 点群 1 件かつ設計 0 件 → 点群のみモード（AC-1）
 * - 点群 1 件かつ設計 1 件 → 標準モード。ただし点群のみ希望があれば点群のみモード（AC-35）
 * - 点群 0 件 / 2 件以上、設計 2 件以上 → 開始不可（AC-2）
 */
export const inspectVisibleTerrain = (
	input: InspectVisibleTerrainInput,
): RoadAiDataInspection => {
	const pointClouds = Array.isArray(input.pointClouds) ? input.pointClouds : [];
	const designs = Array.isArray(input.designs) ? input.designs : [];

	if (pointClouds.length === 0) {
		return { status: "blocked", reason: "POINT_CLOUD_NONE" };
	}
	if (pointClouds.length > 1) {
		return { status: "blocked", reason: "POINT_CLOUD_MULTIPLE" };
	}
	if (designs.length > 1) {
		return { status: "blocked", reason: "DESIGN_MULTIPLE" };
	}

	// biome-ignore lint/style/noNonNullAssertion: length checked above
	const pointCloud = toPointCloudRef(pointClouds[0]!);
	const design = designs.length === 1 ? designs[0] : undefined;

	if (!design || input.pointCloudOnlyRequested === true) {
		return {
			status: "ok",
			selection: { mode: "point_cloud_only", pointCloud },
		};
	}

	return {
		status: "ok",
		selection: {
			mode: "standard",
			pointCloud,
			design: toDesignRef(design),
		},
	};
};

/** 検査結果からモードを取り出す（開始不可のときは null）。 */
export const inspectionMode = (
	inspection: RoadAiDataInspection,
): RoadAiDataSelection["mode"] | null =>
	inspection.status === "ok" ? inspection.selection.mode : null;

const toPointCloudRef = (
	pointCloud: VisiblePointCloudLike,
): RoadAiPointCloudRef => ({
	pointCloudId: Number(pointCloud.getId()),
	assetId: String(pointCloud.getAssetId()),
	name: pointCloud.getName(),
});

const toDesignRef = (design: VisibleDesignLike): RoadAiDesignRef => ({
	designId: String(design.getId()),
	assetId: String(design.getAssetId()),
	name: design.getName(),
});
